import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Loader from "../components/Loader";
import Pagination from "../components/Pagination";
import CreatePlayerModal from "../components/CreatePlayerModal";
import ViewPlayerModal from "../components/ViewPlayerModal";

import playerService from "../services/playerService";
import { formatDateTime } from "../utils/dateUtils";

const ITEMS_PER_PAGE = 8;

const Players = () => {
  const [sidebarOpen, setSidebarOpen] =
    useState(false);

  const [players, setPlayers] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [searchQuery, setSearchQuery] =
    useState("");

  const [currentPage, setCurrentPage] =
    useState(1);

  const [showCreateModal, setShowCreateModal] =
    useState(false);

  const [selectedPlayer, setSelectedPlayer] =
    useState(null);

  const [statusUpdatingId, setStatusUpdatingId] =
    useState(null);

  const handleTogglePlayerStatus = async (player) => {
    const currentStatus = String(player?.status || "ACTIVE").toUpperCase();
    const nextStatus = currentStatus === "ACTIVE" ? "BLOCKED" : "ACTIVE";

    try {
      setStatusUpdatingId(player.id);

      const response = await playerService.updatePlayerStatus(
        player.id,
        nextStatus
      );

      if (!response?.success) {
        toast.error(
          response?.message || "Unable to update Player status."
        );
        return false;
      }

      toast.success(
        response.message || `Player ${nextStatus.toLowerCase()} successfully.`
      );

      await fetchPlayers({ silent: true });
      return true;
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Unable to update Player status."
      );
      return false;
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen((previous) => !previous);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const formatCoins = (value) => {
    return Number(value || 0).toLocaleString(
      "en-IN",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    );
  };

  const fetchPlayers = async ({
    silent = false,
  } = {}) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const response =
        await playerService.getPlayers();

      if (!response?.success) {
        setPlayers([]);

        toast.error(
          response?.message ||
          "Unable to load Players."
        );
        return;
      }

      setPlayers(
        Array.isArray(response.players)
          ? response.players
          : []
      );
    } catch (error) {
      console.error(
        "Load Players Error:",
        error
      );

      toast.error(
        error.response?.data?.message ||
        "Unable to load Players."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchPlayers();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const filteredPlayers = useMemo(() => {
    const query = searchQuery
      .trim()
      .toLowerCase();

    if (!query) {
      return players;
    }

    return players.filter((player) => {
      return (
        String(player.full_name || "")
          .toLowerCase()
          .includes(query) ||
        String(player.username || "")
          .toLowerCase()
          .includes(query) ||
        String(player.mobile || "")
          .toLowerCase()
          .includes(query) ||
        String(player.status || "")
          .toLowerCase()
          .includes(query)
      );
    });
  }, [players, searchQuery]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredPlayers.length /
      ITEMS_PER_PAGE
    )
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const currentPlayers = useMemo(() => {
    const startIndex =
      (currentPage - 1) * ITEMS_PER_PAGE;

    return filteredPlayers.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE
    );
  }, [filteredPlayers, currentPage]);

  const activePlayers = players.filter(
    (player) =>
      String(player.status).toUpperCase() ===
      "ACTIVE"
  ).length;

  const blockedPlayers =
    players.length - activePlayers;

  const totalPlayerWallet = players.reduce(
    (total, player) =>
      total + Number(player.wallet || 0),
    0
  );

  const startRecord =
    filteredPlayers.length === 0
      ? 0
      : (currentPage - 1) *
      ITEMS_PER_PAGE +
      1;

  const endRecord = Math.min(
    currentPage * ITEMS_PER_PAGE,
    filteredPlayers.length
  );

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={closeSidebar}
        />

        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            <div className="admin-page-header">
              <div>
                <span className="admin-page-kicker">SHOP NETWORK</span>
                <h2>Players Management</h2>

                <p>
                  View and create Players registered under your Shop.
                </p>
              </div>

              <div className="d-flex flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() =>
                    fetchPlayers({
                      silent: true,
                    })
                  }
                  disabled={loading || refreshing}
                >
                  {refreshing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise me-2" />
                      Refresh
                    </>
                  )}
                </button>

                <Link
                  to="/transfer-player"
                  className="btn btn-outline-success"
                >
                  <i className="bi bi-send me-2" />
                  Transfer Coins
                </Link>

                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() =>
                    setShowCreateModal(true)
                  }
                >
                  <i className="bi bi-person-plus-fill me-2" />
                  Create Player
                </button>
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-primary text-white rounded-3 p-3 me-3">
                      <i className="bi bi-people fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Total Players
                      </div>

                      <h3 className="fw-bold mb-0">
                        {players.length}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-success text-white rounded-3 p-3 me-3">
                      <i className="bi bi-check-circle fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Active Players
                      </div>

                      <h3 className="fw-bold text-success mb-0">
                        {activePlayers}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-danger text-white rounded-3 p-3 me-3">
                      <i className="bi bi-x-circle fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Blocked Players
                      </div>

                      <h3 className="fw-bold text-danger mb-0">
                        {blockedPlayers}
                      </h3>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-sm-6 col-xl-3">
                <div className="admin-stat-card h-100">
                  <div className="card-body d-flex align-items-center">
                    <div className="bg-warning text-dark rounded-3 p-3 me-3">
                      <i className="bi bi-wallet2 fs-3" />
                    </div>

                    <div>
                      <div className="small text-secondary fw-semibold">
                        Player Wallet Total
                      </div>

                      <h5 className="fw-bold mb-0">
                        🪙{" "}
                        {formatCoins(
                          totalPlayerWallet
                        )}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="admin-panel mb-4">
              <div className="card-body p-3">
                <div
                  className="input-group"
                  style={{ maxWidth: "500px" }}
                >
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search" />
                  </span>

                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search name, username, mobile or status..."
                    value={searchQuery}
                    onChange={(event) =>
                      setSearchQuery(
                        event.target.value
                      )
                    }
                  />

                  {searchQuery && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        setSearchQuery("")
                      }
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="admin-panel overflow-hidden">
              <div className="admin-panel-head">
                <h5 className="fw-bold mb-1">
                  Players List
                </h5>

                <p className="small text-secondary mb-0">
                  Click any Player row to view details and available actions.
                </p>
              </div>

              <div className="card-body p-0">
                {loading ? (
                  <Loader message="Loading Players..." />
                ) : currentPlayers.length === 0 ? (
                  <div className="text-center py-5 px-3">
                    <i className="bi bi-people display-4 text-secondary" />

                    <h5 className="fw-bold mt-3">
                      No Players Found
                    </h5>

                    <p className="text-secondary mb-0">
                      {searchQuery
                        ? "No Player matches your search."
                        : "Create your first Player account."}
                    </p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table admin-table table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-4">
                            ID
                          </th>
                          <th>Player</th>
                          <th>Mobile</th>
                          <th>Wallet</th>
                          <th>Created</th>
                          <th className="pe-4">
                            Status
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {currentPlayers.map(
                          (player) => (
                            <tr
                              key={player.id}
                              onClick={() => setSelectedPlayer(player)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  setSelectedPlayer(player);
                                }
                               }}
                              role="button"
                              tabIndex={0}
                              title="Click to view Player details"
                              style={{ cursor: "pointer" }}
                            >
                              <td className="ps-4 fw-bold">
                                #{player.id}
                              </td>

                              <td>
                                <div className="fw-semibold">
                                  {player.full_name ||
                                    "N/A"}
                                </div>

                                <div className="small text-primary">
                                  @
                                  {player.username ||
                                    "N/A"}
                                </div>
                              </td>

                              <td>
                                {player.mobile || "N/A"}
                              </td>

                              <td className="fw-bold text-success">
                                🪙{" "}
                                {formatCoins(
                                  player.wallet
                                )}
                              </td>

                              <td className="small text-secondary">
                                {formatDateTime(player?.created_at)}
                              </td>

                              <td className="pe-4">
                                <div className="d-flex align-items-center justify-content-between gap-3">
                                  <span
                                    className={`badge rounded-pill px-3 py-2 ${player.status ===
                                      "ACTIVE"
                                      ? "bg-success-subtle text-success border border-success-subtle"
                                      : "bg-danger-subtle text-danger border border-danger-subtle"
                                      }`}
                                  >
                                    {player.status ||
                                      "UNKNOWN"}
                                  </span>

                                  <i className="bi bi-chevron-right text-secondary small" />
                                </div>
                              </td>
                            </tr>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {!loading &&
                filteredPlayers.length > 0 && (
                  <div className="card-footer bg-white border-top p-3">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                      <div className="small text-secondary">
                        Showing {startRecord}–
                        {endRecord} of{" "}
                        {filteredPlayers.length} Players
                      </div>

                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={
                          setCurrentPage
                        }
                      />
                    </div>
                  </div>
                )}
            </div>
          </div>
        </main>
      </div>

      <CreatePlayerModal
        show={showCreateModal}
        onClose={() =>
          setShowCreateModal(false)
        }
        onPlayerCreated={() =>
          fetchPlayers({
            silent: true,
          })
        }
      />

      <ViewPlayerModal
        show={Boolean(selectedPlayer)}
        player={selectedPlayer}
        onClose={() =>
          setSelectedPlayer(null)
        }
        onToggleStatus={handleTogglePlayerStatus}
        isUpdatingStatus={statusUpdatingId === selectedPlayer?.id}
      />
    </div>
  );
};

export default Players;